import React, { useState, useContext, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import { useBowtieLanguage, useBowtieStated } from '../../../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../../../store'
import { ChartEvent, IBarrier, IBarrierTypeIcon, IZoomLevels } from '../../../../../../../types'
import { SpecialStatus } from '../../../../../../../types/enums'
import { Icon } from '../../../../../../../utils'
import {
    barrierWidth,
    barrierHeight,
    unfocusOpacity,
    escalationHeight,
    escalationWidth
} from '../../../../../../../utils/constants'
import { BowtieStatusContext } from '../../../../../../BowtieStatusContext'
import { BarrierView } from '../../../../../../BowtieViews'
import { EscalationFactor } from './components'
import { TextContainer } from './styles'

interface IProps {
    x: number
    y: number
    uid: string
    type: 'threat' | 'consequence'
    barrierData: IBarrier
    /** NOTE: data id is `bar-${barrierData.id}` */
    dataId: string
    event: BarrierView
    barrierBrothersY: number
    rootXIndex: number
    zoomLevel: IZoomLevels
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}

export const Barrier: React.FC<IProps> = ({
    barrierData,
    uid,
    event: view,
    type,
    x,
    y,
    barrierBrothersY = 0,
    rootXIndex,
    zoomLevel,
    barrierTypeIconMap
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const i18n = useBowtieLanguage()
    const statusContext = useContext(BowtieStatusContext)
    const {
        consequencesExpandedBarriers,
        threatsExpandedBarriers,
        highlightDegradedBarriers,
        selectedBarrier,
        searchDataId,
        barrierIds: mandatoryBarrierIds
    } = useSelector((state: IReduxReducers) => ({
        ...state.bowtieExpandedBarriers,
        ...state.highlightDegradedBarriers,
        ...state.barrierSelectedToModal,
        ...state.searchBarrier,
        ...state.mandatoryBarrier
    }))

    const [stateExpanded, setStateExpanded] = useState(false)

    const event = useBowtieStated(view)
    const getBackgroundColor = (): string => {
        if (zoomLevel === 'level0' || zoomLevel === 'level1') {
            return statusContext.getBarrierStatusColorById(event?.status_id)
        }

        return theme['gray-0']
    }
    const isUnknowEvent = (barrier: IBarrier, event: ChartEvent): boolean => {
        if (
            barrier.barrierType?.barrierTypeSpecial !== SpecialStatus.UNMONITORED &&
            event?.status === SpecialStatus.UNMONITORED
        ) {
            return true
        } else {
            return false
        }
    }

    const handleGetBarrierTypeIconById = (): IBarrierTypeIcon => {
        if (barrierTypeIconMap) {
            if (barrierData?.barrierType?.barrierTypeIcon?.id) {
                return barrierTypeIconMap.get(barrierData.barrierType.barrierTypeIcon.id)
            }
        }

        return null
    }

    function openModal(): void {
        dispatch<IReduxActions['barrierSelectedToModal']>({
            type: 'SET_SELECTED_BARRIER',
            payload: {
                newSelectedBarrier: barrierData,
                newEvent: event,
                newColor: isUnknowEvent(barrierData, event)
                    ? statusContext.getBarrierStatusColorById(null)
                    : statusContext.getBarrierStatusColorById(event?.status_id),
                newStatusName: isUnknowEvent(barrierData, event)
                    ? statusContext.getBarrierStatusNameById(null)
                    : statusContext.getBarrierStatusNameById(event?.status_id)
            }
        })
    }

    const toggleEscalation = (): void => {
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (barrierData.escalations.filter(el => el).length) {
            if (expandedBarriers?.length > 0) {
                if (stateExpanded) {
                    dispatch<IReduxActions['bowtieExpandedBarriers']>({
                        type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                        payload: {
                            newExpandedBarriers: expandedBarriers.filter(el => !el.barrierId.startsWith(uid))
                        }
                    })
                    setStateExpanded(false)
                } else {
                    const index = barrierData.escalations.length ?? 0
                    const newExpanded = []
                    for (let i = 0; i < index; i++) {
                        newExpanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${uid}|esc-${barrierData.escalations[i].id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength: number = barrierBrothersY + newExpanded.reduce((a, b) => a + b, 0)
                    dispatch<IReduxActions['bowtieExpandedBarriers']>({
                        type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                        payload: {
                            newExpandedBarriers: [
                                ...expandedBarriers,
                                {
                                    barrierId: uid,
                                    rootXIndex: rootXIndex,
                                    escalations:
                                        barrierData.escalations?.map((e, i) => ({
                                            escId: `${uid}|esc-${e.id}`,
                                            x: x + (type === 'threat' ? -escalationWidth : escalationWidth),
                                            y:
                                                y +
                                                escalationHeight +
                                                barrierHeight +
                                                (brothersLength + i) * (barrierHeight * 2 + 30)
                                        })) ?? []
                                }
                            ]
                        }
                    })
                    setStateExpanded(true)
                }
            }
        }
    }

    useEffect(() => {
        if (selectedBarrier === barrierData) {
            dispatch<IReduxActions['barrierSelectedToModal']>({
                type: 'SET_SELECTED_BARRIER',
                payload: {
                    newSelectedBarrier: barrierData,
                    newEvent: event,
                    newColor: isUnknowEvent(barrierData, event)
                        ? statusContext.getBarrierStatusColorById(null)
                        : statusContext.getBarrierStatusColorById(event?.status_id),
                    newStatusName: isUnknowEvent(barrierData, event)
                        ? statusContext.getBarrierStatusNameById(null)
                        : statusContext.getBarrierStatusNameById(event?.status_id)
                }
            })
        }
    }, [event?.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (searchDataId?.startsWith(`${uid}|`)) {
            if (!stateExpanded) {
                setTimeout(() => toggleEscalation(), 100)
            }
        } else if (searchDataId === uid) {
            dispatch<IReduxActions['searchBarrier']>({
                type: 'SET_SEARCH_BARRIER_POSITION',
                payload: {
                    newSearchPosition: {
                        x: type === 'threat' ? x + barrierWidth / 2 : x - barrierWidth / 2,
                        y: y + barrierHeight / 2
                    }
                }
            })
        }
    }, [searchDataId]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            {stateExpanded &&
                view.escalationFactorsView?.map((escalationView, index, arr) => {
                    const escalation = escalationView.escalationFactor
                    const expanded = []
                    const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

                    for (let i = 0; i < index; i++) {
                        expanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${uid}|esc-${arr[i].escalationFactor.id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength = barrierBrothersY + expanded.reduce((a, b) => a + b, 0)
                    const lineY = y + barrierHeight * 2 + (brothersLength + index) * (barrierHeight * 2 + 30)

                    return (
                        <EscalationFactor
                            key={`${uid}|esc-${escalation.id}`}
                            uid={`${uid}|esc-${escalation.id}`}
                            escalationData={escalation}
                            dataId={`esc-${escalation.id}`}
                            type={type}
                            event={escalationView}
                            x={type === 'threat' ? x - 150 : x + 150}
                            y={lineY}
                            xFather={type === 'consequence' ? x - barrierWidth : x}
                            yFather={y}
                            rootXIndex={rootXIndex}
                            zoomLevel={zoomLevel}
                            barrierTypeIconMap={barrierTypeIconMap}
                        />
                    )
                })}
            <g
                id={uid}
                transform={`translate(${type === 'consequence' ? x - barrierWidth : x}, ${y})`}
                cursor="pointer"
                fill={theme['gray-0']}
                opacity={
                    highlightDegradedBarriers
                        ? !event || event?.status === SpecialStatus.INACTIVE
                            ? 1
                            : unfocusOpacity
                        : 1
                }
            >
                <rect
                    x={barrierWidth / 2 - 3}
                    y={-15}
                    width={6}
                    height={25}
                    fill={theme['gray-500']}
                    className={`${uid} linkx`}
                />
                <rect
                    width={barrierWidth}
                    height={zoomLevel === 'level2' ? barrierHeight : 0}
                    stroke={
                        isUnknowEvent(barrierData, event)
                            ? statusContext.getBarrierStatusColorById(null)
                            : statusContext.getBarrierStatusColorById(event?.status_id)
                    }
                    strokeWidth={1}
                    fill={getBackgroundColor()}
                    className={`${uid} background`}
                />
                <rect
                    width={barrierWidth}
                    height={14}
                    fill={
                        isUnknowEvent(barrierData, event)
                            ? statusContext.getBarrierStatusColorById(null)
                            : statusContext.getBarrierStatusColorById(event?.status_id)
                    }
                    className={`${uid} header`}
                />

                {/* this is the box of Icon that expand to modal */}
                <text
                    opacity={zoomLevel === 'level0' ? 0 : 1}
                    x={2}
                    y={2}
                    textAnchor="start"
                    dominantBaseline="hanging"
                    fontSize={10}
                    style={{ fontFamily: theme['font-bold'], fill: theme.static['gray-0'] }}
                    className={`${uid} status`}
                >
                    {i18n(
                        isUnknowEvent(barrierData, event)
                            ? statusContext.getBarrierStatusNameById(null)
                            : statusContext.getBarrierStatusNameById(event?.status_id)
                    )}
                </text>
                <Icon
                    x={
                        barrierData?.criticality?.level !== 'HIGH' && !barrierData?.manualInteraction
                            ? barrierWidth - 14
                            : barrierData?.criticality?.level === 'HIGH' && barrierData?.manualInteraction
                            ? barrierWidth - 41
                            : barrierWidth - 29
                    }
                    y={0}
                    type={barrierData.barrierType.iconReference && barrierData.barrierType.iconReference}
                    barrierTypeIcon={handleGetBarrierTypeIconById()}
                    scale="0.8"
                    color={theme.static['gray-0']}
                    opacity={zoomLevel === 'level0' ? 0 : 1}
                    className={`${uid} barrier-type-icon`}
                />
                {mandatoryBarrierIds.includes(barrierData.id) && (
                    <Icon
                        x={83}
                        y={34}
                        type="mandatory"
                        color={theme.static['gray-600']}
                        opacity={zoomLevel === 'level0' || zoomLevel === 'level1' ? 0 : 1}
                        scale="0.8"
                        className={`${uid} barrier-mandatory-icon`}
                    />
                )}

                {barrierData.barrierType.barrierTypeSpecial !== SpecialStatus.UNMONITORED &&
                    barrierData.criticality?.level &&
                    barrierData.criticality.level === 'HIGH' && (
                        <Icon
                            x={barrierData?.manualInteraction ? barrierWidth - 28 : barrierWidth - 14}
                            y={0}
                            type="critical"
                            color={theme.static['gray-0']}
                            opacity={zoomLevel === 'level0' ? 0 : 1}
                            scale="0.8"
                            className={`${uid} barrier-critical-icon`}
                        />
                    )}

                {barrierData?.manualInteraction && (
                    <Icon x={barrierWidth - 14} type="manual" color={theme.static['gray-0']} scale="0.8" />
                )}

                {zoomLevel === 'level2' && (
                    <>
                        <foreignObject
                            x={4}
                            y={14 + 4}
                            width={barrierWidth - 8}
                            height={barrierHeight - 14 - 8}
                            style={{ overflow: 'hidden' }}
                        >
                            <TextContainer>{barrierData?.name ?? i18n('Barrier')}</TextContainer>
                        </foreignObject>
                        <rect
                            width={barrierWidth}
                            height={barrierHeight}
                            opacity={0}
                            cursor="pointer"
                            onClick={e => {
                                openModal()
                                e.stopPropagation()
                            }}
                        />
                    </>
                )}
                {barrierData?.escalations?.length > 0 && (
                    <Icon
                        type="arrow"
                        x={barrierWidth / 2 - 8}
                        y={(zoomLevel === 'level2' ? barrierHeight : 15) + (stateExpanded ? 20 : 4)}
                        scale={stateExpanded ? '1 -1' : '1'}
                        color={
                            isUnknowEvent(barrierData, event)
                                ? statusContext.getBarrierStatusColorById(null)
                                : statusContext.getBarrierStatusColorById(event?.status_id)
                        }
                        auxliarColor={theme['gray-200']}
                        clickEvent={toggleEscalation}
                        className={`${uid} escalation-icon`}
                    />
                )}
            </g>
        </>
    )
}
